import { forwardRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const programs = [
  { value: "in-person", label: "In-Person Coaching (1-on-1)" },
  { value: "hybrid", label: "Hybrid Coaching" },
  { value: "online", label: "Online Coaching" },
  { value: "bootcamp", label: "Unlimited Bootcamp" },
];

const inputCls =
  "w-full bg-[#111111] border border-[#222] text-white placeholder:text-[#555] px-4 py-4 text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA] focus:border-[#005EEA] transition-shadow";

const LeadForm = forwardRef(({ prefill }, ref) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", program: "", goal: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({
        ...f,
        program: prefill.program || f.program,
        message: prefill.note ? prefill.note : f.message,
      }));
    }
  }, [prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.program) {
      toast("Please pick a program to continue.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast("Request sent!", { description: "Check your inbox — Coach Jean Claude will be in touch within 24h." });
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="consult"
      ref={ref}
      className="bg-[#050505] py-24 md:py-40 border-t border-[#222]"
      data-testid="consult"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <p className="font-mono-accent text-[#005EEA] text-xs mb-4">Start Here</p>
            <h2 className="font-display text-white text-5xl md:text-7xl leading-[0.88] mb-8">
              Book your free consult
            </h2>
            <p className="text-[#A0A0A0] text-lg leading-relaxed max-w-md">
              Tell me about your goals. I'll personally reach out within 24 hours to plan your first
              session. No commitment, no pressure.
            </p>
          </Reveal>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#005EEA]/40 bg-[#0b0f1a] p-10 flex flex-col items-start gap-4"
              data-testid="lead-success"
            >
              <CheckCircle2 className="text-[#005EEA]" size={40} />
              <h3 className="font-display text-white text-3xl leading-none">You're in.</h3>
              <p className="text-[#A0A0A0]">
                Thanks {form.name.split(" ")[0]} — your request is locked in. Watch your inbox for a
                confirmation. Let's get to work.
              </p>
            </motion.div>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    className={inputCls}
                    placeholder="Full name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    data-testid="lead-name"
                  />
                  <input
                    className={inputCls}
                    placeholder="Phone"
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    data-testid="lead-phone"
                  />
                </div>
                <input
                  className={inputCls}
                  type="email"
                  placeholder="Email address"
                  required
                  value={form.email}
                  onChange={set("email")}
                  data-testid="lead-email"
                />
                <Select value={form.program} onValueChange={(v) => setForm((f) => ({ ...f, program: v }))}>
                  <SelectTrigger
                    className="w-full bg-[#111111] border border-[#222] text-white rounded-none py-6 px-4 focus:ring-2 focus:ring-[#005EEA] data-[placeholder]:text-[#555]"
                    data-testid="lead-program"
                  >
                    <SelectValue placeholder="Which program interests you?" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border border-[#222] text-white rounded-none">
                    {programs.map((p) => (
                      <SelectItem key={p.value} value={p.value} className="rounded-none focus:bg-[#005EEA] focus:text-white">
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input
                  className={inputCls}
                  placeholder="Your #1 goal (e.g. lose 10kg, get stronger)"
                  required
                  value={form.goal}
                  onChange={set("goal")}
                  data-testid="lead-goal"
                />
                <textarea
                  className={`${inputCls} min-h-[120px] resize-none`}
                  placeholder="Anything else I should know? (optional)"
                  value={form.message}
                  onChange={set("message")}
                  data-testid="lead-message"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full font-mono-accent text-xs bg-[#005EEA] text-white px-6 py-6 flex items-center justify-center gap-2 hover:bg-[#0047b3] transition-colors disabled:opacity-60 shadow-[0_0_30px_rgba(0,94,234,0.3)]"
                  data-testid="lead-submit"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : "Request Free Consultation"}
                </button>
                <p className="text-[#555] text-xs text-center leading-relaxed">
                  By submitting, you agree to be contacted about coaching. See our{" "}
                  <a href="/privacy" className="text-[#888] hover:text-[#005EEA] underline">
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
});

LeadForm.displayName = "LeadForm";
export default LeadForm;
